import { lazy, Suspense } from "react";
import { Center, Loader } from "@mantine/core";
import { JwtProvider } from "@funded-here-interface/common/src/providers/JwtProvider";
import {
  resetAuthState,
  setToken,
} from "@funded-here-interface/common/src/features/auth/authSlice_Admin";
import "./App.css";
import { PersistGate } from "redux-persist/integration/react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { QueryClientProvider } from "react-query";
import { store, persistor, RootState } from "./store";
import { Provider } from "react-redux";
import { client } from "./reactQuery";

const AcknowledgementLayout = lazy(
  () =>
    import("@funded-here-interface/common/src/Layouts/AcknowledgementLayout/AcknowledgementLayout"),
);
const LoginLayout = lazy(
  () =>
    import("@funded-here-interface/common/src/Layouts/LoginLayout/LoginLayout"),
);
const PasswordReset = lazy(
  () =>
    import("@funded-here-interface/common/src/pages/ForgetPassword/PasswordReset"),
);
const PasswordResetMsg = lazy(
  () =>
    import("@funded-here-interface/common/src/pages/ForgetPassword/PasswordResetMsg"),
);
const Navbar = lazy(
  () =>
    import("@funded-here-interface/common/src/components/MobileNavbar/Navbar"),
);
const TwoFAQRPageCommon = lazy(
  () => import("@funded-here-interface/common/src/pages/TwoFA/TwoFAQRCode"),
);
const OperationAndRiskLayout = lazy(
  () => import("./layouts/OperationAndRisk/OperationAndRiskLayout"),
);
const ProductLayout = lazy(
  () => import("./layouts/ProductLayout/ProductLayout"),
);
const SalesSupportLayout = lazy(
  () => import("./layouts/SalesSupportLayout/SalesSupportLayout"),
);

const StructuredNotePage = lazy(
  () => import("./screens/StructuredNote/StructuredNote"),
);
const OrgDetailsPage = lazy(
  () => import("./screens/OrgDetails/OrgDetailsPage"),
);
const Issusingspv = lazy(() => import("./screens/IssusingSPV.tsx/Issusingspv"));
const Dashboard = lazy(() => import("./screens/PMDashboard/DashBoard"));
const CampaignList = lazy(() => import("./screens/CampaignList/CampaignList"));
const CreateCampaign = lazy(
  () => import("./screens/CreateCampaign/CreateCampaign"),
);
const Login = lazy(() => import("./screens/Login/Login"));
const Notelist = lazy(() => import("./screens/Notelist/Notelist"));
const PmVerifySkuLoans = lazy(
  () => import("./screens/PmVerifySkuLoans/PmVerifySkuLoans"),
);
const IndividualList = lazy(
  () => import("./screens/IndividualList/IndividualList"),
);
const InvestorSummary = lazy(
  () => import("./screens/PMDashboard/Components/Investor/InvestorSummary"),
);
const Merchant = lazy(
  () => import("./screens/PMDashboard/Components/MerchantSummary/Merchant"),
);
const Skusummary = lazy(
  () => import("./screens/PMDashboard/Components/SKUSummary/Skusummary"),
);
const VirtualAccounts = lazy(
  () => import("./screens/VirtualAccounts/VirtualAccounts"),
);
const LoanDetails = lazy(() => import("./screens/LoanDetails/LoanDetails"));
const PmVerifySkuLoanDetails = lazy(
  () => import("./screens/PmVerifySkuLoanDetails/PmVerifySkuLoanDetails"),
);
const Repayment = lazy(() => import("./screens/PMRepayment/Repayment"));
const IssueNote = lazy(() => import("./screens/IssueSNOverview/IssueNote"));
const SkuLoanApplication = lazy(
  () => import("./screens/SkuLoanApplication/SkuLoanApplication"),
);
const Withdraw = lazy(() => import("./screens/Withdraw/Withdraw"));
const FeatureFlag = lazy(() => import("./screens/FeatureFlag/FeatureFlag"));
const ExternalAccounts = lazy(
  () => import("./screens/ExternalAccounts/ExternalAccounts"),
);
const Audit = lazy(() => import("./screens/Audit/Audit"));
const Inventory = lazy(
  () => import("./screens/InventoryOrders/Inventory/Inventory"),
);
const Orders = lazy(() => import("./screens/InventoryOrders/Orders/Orders"));
const FundPull = lazy(
  () => import("./screens/InventoryOrders/FundPull/FundPull"),
);
const FundPullAutoRefund = lazy(
  () => import("./screens/MerchantConfiguration/FundPullAutoRefund"),
);
const Scheme = lazy(() => import("./screens/Scheme/Scheme"));

const PrivateRoute = ({ children }: any) => {
  const token = store.getState().auth.token;

  return !token || token === "none" ? <Navigate to={"/login"} /> : children;
};

function App() {
  return (
    <QueryClientProvider client={client}>
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <BrowserRouter>
            <JwtProvider
              tokenSelector={(state: RootState) => state.auth}
              resetAuthAction={resetAuthState}
              setTokenAction={setToken}
              persistor={persistor}
            >
              <Suspense
                fallback={
                  <Center style={{ height: "100vh" }}>
                    <Loader size="lg" />
                  </Center>
                }
              >
                <Routes>
                  <Route
                    path="*"
                    Component={() => <Navigate to={"/login"} />}
                  />
                  <Route
                    path="/login"
                    element={<LoginLayout content={<Login />} />}
                  />
                  <Route
                    path="/two-auth-QR-code"
                    element={<Navbar content={<TwoFAQRPageCommon />} />}
                  />
                  <Route
                    path="/password-reset"
                    element={
                      <AcknowledgementLayout content={<PasswordReset />} />
                    }
                  />
                  <Route
                    path="/reset-message"
                    element={
                      <AcknowledgementLayout content={<PasswordResetMsg />} />
                    }
                  />
                  <Route
                    path="/pm-dashboard/:subpath"
                    element={
                      <PrivateRoute>
                        <ProductLayout content={<Dashboard />} />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/withdrawal"
                    element={
                      <PrivateRoute>
                        <ProductLayout content={<Withdraw />} />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/investorsummary"
                    element={
                      <PrivateRoute>
                        <ProductLayout content={<InvestorSummary />} />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/merchant"
                    element={
                      <PrivateRoute>
                        <ProductLayout content={<Merchant />} />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/snusummary"
                    element={
                      <PrivateRoute>
                        <ProductLayout content={<Skusummary />} />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/repayment/:id"
                    element={
                      <PrivateRoute>
                        <ProductLayout content={<Repayment />} />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/note-list"
                    element={
                      <PrivateRoute>
                        <ProductLayout content={<Notelist />} />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/create-campaign"
                    element={
                      <PrivateRoute>
                        <ProductLayout content={<CreateCampaign />} />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/org-list"
                    element={
                      <PrivateRoute>
                        <OperationAndRiskLayout content={<OrgDetailsPage />} />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/individual-list"
                    element={
                      <PrivateRoute>
                        <OperationAndRiskLayout content={<IndividualList />} />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/campaign-list"
                    element={
                      <PrivateRoute>
                        <ProductLayout content={<CampaignList />} />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/loan-details"
                    element={
                      <PrivateRoute>
                        <ProductLayout content={<LoanDetails />} />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/verify-sku-loans"
                    element={
                      <PrivateRoute>
                        <ProductLayout content={<PmVerifySkuLoans />} />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/verify-sku-loan-details"
                    element={
                      <PrivateRoute>
                        <ProductLayout content={<PmVerifySkuLoanDetails />} />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/issue-note/:id"
                    element={
                      <PrivateRoute>
                        <ProductLayout content={<IssueNote />} />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/issuisng-spv"
                    element={
                      <PrivateRoute>
                        <ProductLayout content={<Issusingspv />} />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/structured-note/:tabValue"
                    element={
                      <PrivateRoute>
                        <ProductLayout content={<StructuredNotePage />} />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/virtual-accounts"
                    element={
                      <PrivateRoute>
                        <ProductLayout content={<VirtualAccounts />} />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/external-accounts"
                    element={
                      <PrivateRoute>
                        <ProductLayout content={<ExternalAccounts />} />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/sales-support/:tabValue"
                    element={
                      <PrivateRoute>
                        <SalesSupportLayout content={<SkuLoanApplication />} />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/audit"
                    element={
                      <PrivateRoute>
                        <ProductLayout content={<Audit />} />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/feature-flags"
                    element={
                      <PrivateRoute>
                        <ProductLayout content={<FeatureFlag />} />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/inventory"
                    element={
                      <PrivateRoute>
                        <ProductLayout content={<Inventory />} />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/orders"
                    element={
                      <PrivateRoute>
                        <ProductLayout content={<Orders />} />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/fund-pull"
                    element={
                      <PrivateRoute>
                        <ProductLayout content={<FundPull />} />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/fund-pull-auto-refund"
                    element={
                      <PrivateRoute>
                        <ProductLayout content={<FundPullAutoRefund />} />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/schemes"
                    element={
                      <PrivateRoute>
                        <ProductLayout content={<Scheme />} />
                      </PrivateRoute>
                    }
                  />
                </Routes>
              </Suspense>
            </JwtProvider>
          </BrowserRouter>
        </PersistGate>
      </Provider>
    </QueryClientProvider>
  );
}
export default App;
